import React, { useEffect, useMemo, useState } from 'react';
import { connect, useSelector } from 'react-redux';
import { LocalNotification } from '@amzn/aac-chrome-internal-api';
import { RootState } from 'src/app/store';
import { useAcesBanners } from '../useAcesBanners.hook';
import {
  BANNER_LEVEL,
  BANNER_TYPE,
  PAGE_LAYOUT_TYPE,
  selectPageLayoutType,
  selectPageLevelBanners,
} from 'src/app/slice';
import { bannerLocalStorageKey, bannerPlacementType } from '../types';
import { ConnectedBanner } from './Banner';
import { selectHieConsoleLevelBannerCdn } from 'src/features/header/store/slice';
import { PageBannerContainer } from '../styles';
import { PageHeaderWrapper } from '../../PageHeader/styles';
import { useStardustBanners } from '../useStardustBanners.hook';
import { useIsFeatureEnabled } from 'src/commons/hooks';
import { WeblabedFeatures } from 'src/commons/constants';
import { useHeaderBundle } from 'src/providers/PantherLocalizationProvider';
import { unsupportedBrowserBanner } from 'src/constants/unsupported-browser-banner';
import { containsUnsupportedCSS } from 'src/utils/browser';

interface BannerWithAcesProps {
  banners: LocalNotification[];
  bannerLevel: BANNER_LEVEL;
  cloudfrontURL: string;
  isMobileDevice?: boolean;
  withPageHeaderWrapper?: boolean;
}

const ACES_REQUEST_TIMEOUT = 1000;

export const BannerWithAces: React.VFC<BannerWithAcesProps> = ({
  banners,
  bannerLevel,
  cloudfrontURL,
  isMobileDevice = false,
  withPageHeaderWrapper = false,
}) => {
  const pageLayoutType = useSelector(selectPageLayoutType);
  const bundle = useHeaderBundle();
  const isStardustBannerFeatureEnabled = useIsFeatureEnabled(
    WeblabedFeatures.STARDUST_BANNER_UI,
  );
  const isFocusLayout = pageLayoutType === PAGE_LAYOUT_TYPE.FOCUS;

  if (bannerLevel === BANNER_LEVEL.CONSOLE && containsUnsupportedCSS()) {
    banners = [
      {
        id: unsupportedBrowserBanner.id,
        type: unsupportedBrowserBanner.type as BANNER_TYPE,
        message: bundle.getMessage(unsupportedBrowserBanner.message),
        link: {
          label: bundle.getMessage(unsupportedBrowserBanner.link.label),
          url: unsupportedBrowserBanner.link.url,
        },
      },
    ];
  }

  const stardustBanners = useStardustBanners(
    bannerPlacementType[bannerLevel],
    cloudfrontURL,
    isStardustBannerFeatureEnabled,
  );

  const { notifications, loading, actions } = useAcesBanners(
    bannerPlacementType[bannerLevel],
  );

  const [shouldRender, setShouldRender] = useState(false);

  const combinedBanners = useMemo(() => {
    const localStorageItems = JSON.parse(
      localStorage.getItem(bannerLocalStorageKey[bannerLevel]) || '[]',
    );
    const returnedBanners = [
      ...banners,
      ...notifications,
      ...stardustBanners.notifications,
    ].filter((banner) => !localStorageItems.includes(banner.id));

    return returnedBanners;
  }, [banners, notifications, stardustBanners]);

  // Set timeout for getting aces notifications
  useEffect(() => {
    const timer = setTimeout(() => {
      setShouldRender(true);
    }, ACES_REQUEST_TIMEOUT);
    return () => clearTimeout(timer);
  }, []);

  // Do not render if aces call is still in progress and timeout not reached
  if (loading && !shouldRender) {
    return null;
  }

  if (combinedBanners.length === 0) {
    return null;
  }

  const renderBanner = () => (
    <ConnectedBanner
      banners={combinedBanners}
      acesDismissFn={actions.dismiss}
      bannerLevel={bannerLevel}
    />
  );

  // Render page level banner
  if (bannerLevel === BANNER_LEVEL.PAGE) {
    if (withPageHeaderWrapper) {
      return (
        <PageHeaderWrapper
          id="pageHeader"
          data-ccx-e2e-id="aac-pageheader"
          isMobileDevice={isMobileDevice}
          hasSectionNav={false}
        >
          <PageBannerContainer isFocusLayout={isFocusLayout}>
            {renderBanner()}
          </PageBannerContainer>
        </PageHeaderWrapper>
      );
    }

    return (
      <PageBannerContainer isFocusLayout={isFocusLayout}>
        {renderBanner()}
      </PageBannerContainer>
    );
  }

  // Render console level banner
  return renderBanner();
};

const ConsoleLevelBannerWithAcesConnector = connect(
  (state: RootState, ownProps) => {
    return {
      ...ownProps,
      banners: [],
      bannerLevel: BANNER_LEVEL.CONSOLE,
      cloudfrontURL: selectHieConsoleLevelBannerCdn(state),
    };
  },
);

export const ConnectedConsoleLevelBannerWithAces =
  ConsoleLevelBannerWithAcesConnector(BannerWithAces);

const PageLevelBannerWithAcesConnector = connect(
  (state: RootState, ownProps) => {
    return {
      ...ownProps,
      banners: selectPageLevelBanners(state),
      bannerLevel: BANNER_LEVEL.PAGE,
      cloudfrontURL: selectHieConsoleLevelBannerCdn(state),
    };
  },
);

export const ConnectedPageLevelBannersWithAces =
  PageLevelBannerWithAcesConnector(BannerWithAces);
